import { Inject, Injectable, InjectionToken, signal } from "@angular/core";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel, RetryContext } from "@microsoft/signalr";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";
import { AuthServiceFacade } from "@pcp/auth";
import { guid } from "@pcp/core/interfaces";
import { ensureError, isNotNullOrUndefined } from "@pcp/core/util";
import { BROWSER_SESSION_ID, PCP_LOGGER, PcpLoggerInterface } from "@pcp/logging";
import { BehaviorSubject, first, lastValueFrom, Subject } from "rxjs";
import { distinctUntilChanged, filter, take } from "rxjs/operators";
import { isDebugEnabled } from "shared/util-runtime-config";
import * as LessonCommands from "../interfaces/lesson-commands";
import { PrefetchImagesInChapterCommand } from "../interfaces/prefetch-commands";
import * as SlideCommands from "../interfaces/slide-commands";
import * as VideoCommands from "../interfaces/video-commands";
import { ClassroomCommand, CommandType } from "../interfaces/streaming";
import { SignalrLessonClient } from "../models/signalr-lesson-client";
import { LessonStateResponse } from "../interfaces/lesson-state-response";
import { WebLockHelper } from "../util/web-lock-helper";
import { lessonGoneReason, LessonStoppedError } from "../errors/lesson-stopped-error";


export const STREAMING_SERVICE_URL = new InjectionToken<string>('streaming-service-url');

@Injectable({
    providedIn: 'root'
})
export class StreamingHubService {

    private readonly joinedLessonId = signal<null|guid>(null);

    private readonly isConnected = new BehaviorSubject<boolean>(false);
    readonly isConnected$ = this.isConnected.asObservable();

    private readonly isReconnecting = new BehaviorSubject<boolean>(false);
    readonly isReconnecting$ = this.isReconnecting.asObservable().pipe(distinctUntilChanged());

    private readonly reconnectToLessonFailed = new Subject<any>();
    readonly reconnectToLessonFailed$ = this.reconnectToLessonFailed.asObservable();

    public readonly connection: HubConnection;
    private readonly webLock = new WebLockHelper();

    private readonly onReconnectedSubject = new Subject<void>();
    /**
     * Emits whenever the SignalR connection has been reconnected
     * -> connection lost + reconnected
     */
    readonly onReconnected$ = this.onReconnectedSubject.asObservable();

    private readonly logger: PcpLoggerInterface;

    /**
     * Guards the unauthorized-reconnect log so a token that expires while the page
     * is left open is reported once, not on every retry. Reset on a successful (re)connect.
     */
    private unauthorizedReconnectLogged = false;

    constructor(
        @Inject(STREAMING_SERVICE_URL) private streamingServiceUrl: string,
        private authService: AuthServiceFacade,
        @Inject(PCP_LOGGER) pcpLogger: PcpLoggerInterface,
        @Inject(BROWSER_SESSION_ID) private readonly browserSessionId: string,
    ) {
        this.logger = pcpLogger.createScoped('streaming-hub');
        // TODO: comment in to reconnect when user goes online
        // window.addEventListener('online', this.handleOnline.bind(this));

        const accessTokenFactory = () => lastValueFrom(
            this.authService.token$.pipe(
                filter(isNotNullOrUndefined),
                take(1),
            )
        );
        const unauthorizedErrorMsg = `Failed to complete negotiation with the server: Error: Unauthorized: Status code '401'`;
        this.connection = new HubConnectionBuilder()
            .withUrl(`${this.streamingServiceUrl}?bsid=${this.browserSessionId}`, {
                accessTokenFactory,
                withCredentials: false,
                // transport: HttpTransportType.WebSockets,
                // Read once at connection-build time — toggling debug requires a reconnect/reload.
                logMessageContent: isDebugEnabled(),
                logger: LogLevel.Warning,
            })
            .configureLogging(isDebugEnabled() ? LogLevel.Trace : LogLevel.Warning)

            // MessagePack protocol for efficient binary data transmission
            // Both JSON and MessagePack are supported - negotiated automatically
            .withHubProtocol(new MessagePackHubProtocol())

            // NOTE: to enable diagnostic clients, uncomment the next line
            // .withUrl(this.streamingServiceUrl + '?diag=yes', { accessTokenFactory: () => accessToken })
            .withStatefulReconnect({ bufferSize: 256 * 1024 }) // 256KB

            .withAutomaticReconnect({
                // default interval is [0, 2, 10 and 30] seconds
                nextRetryDelayInMilliseconds: (retryContext: RetryContext): number | null => {
                    const error = retryContext.retryReason;
                    if ('message' in error && error.message === unauthorizedErrorMsg) {
                        // Token is invalid/expired — typically a page left open until the token
                        // expired, after which reconnect retries indefinitely. The repeated
                        // negotiate 401s are filtered out of Application Insights (see
                        // telemetry-filters.ts); log once here so a genuine, persistent auth
                        // failure stays visible without flooding telemetry on every retry.
                        if (!this.unauthorizedReconnectLogged) {
                            this.unauthorizedReconnectLogged = true;
                            this.logger.error('StreamingHub reconnect unauthorized: access token invalid or expired');
                        }
                    }

                    // first retries with short delays.
                    // Following retries with increasing delays.
                    // Never stop retrying unless the user stops the connection or gets logged out.
                    if (retryContext.previousRetryCount < 4) {
                        return [0, 1000, 1000, 2000][retryContext.previousRetryCount];
                    }

                    if (retryContext.previousRetryCount < 20) {
                        return 3000;
                    }

                    if (retryContext.previousRetryCount < 200) {
                        return 5000;
                    }

                    return 10000;
                }
            })

            // https://learn.microsoft.com/en-us/aspnet/core/signalr/configuration?view=aspnetcore-8.0&tabs=javascript#configure-timeout-and-keep-alive-options
            .withKeepAliveInterval(15000) // default = 15.000
            .withServerTimeout(30000)     // default = 30.000

            .build();

        this.connection.on("ping", () =>
            Promise.resolve("pong")
        );

        this.connection.onreconnecting(error => {
            this.isReconnecting.next(true);
            this.logger.trackEvent('StreamingHub reconnecting', { error });

            this.logger.warn('Reconnecting', { connectionId: this.connection.connectionId });
        });

        this.connection.onreconnected(connectionId => {
            this.unauthorizedReconnectLogged = false;
            this.logger.info('Reconnected', { connectionId });

            const lessonId = this.joinedLessonId();
            if (lessonId) {
                this.joinLesson(lessonId)
                    .catch(error => {
                        this.reconnectToLessonFailed.next(error);
                        // debugger;
                    });
            }


            this.isReconnecting.next(false);
            this.logger.trackEvent('StreamingHub reconnected', { connectionId });
            this.isConnected.next(true);
            this.onReconnectedSubject.next();
        });
        this.connection.onclose(error => {
            this.isReconnecting.next(false);
            this.webLock.release();

            this.logger.trackEvent('StreamingHub connection closed', { error });

            this.isConnected.next(false);
        });
    }

    private async handleOnline(): Promise<void> {
        if (!this.isConnected.value) {
            this.logger.info('User is back online, attempting to reconnect...');
            await this.startConnection();
        }
    }

    public async startConnection(): Promise<void> {
        if (this.connection.state === HubConnectionState.Disconnected) {
            try {
                await this.connection.start();
                this.unauthorizedReconnectLogged = false;
                this.isConnected.next(true);
                this.logger.trackEvent('StreamingHub connected');
                this.logger.info('Connection started.');
                this.webLock.lock();

                // TODO: retry when start failed?
                // https://learn.microsoft.com/de-de/aspnet/core/signalr/javascript-client?view=aspnetcore-9.0&tabs=visual-studio#manually-reconnect
            } catch (err) {
                this.logger.error('Error while starting connection', ensureError(err));
                this.webLock.release();
                throw err;
            }
        }
    }


    public async joinLesson(lessonId: guid): Promise<LessonStateResponse>
    {
        if (this.connection.state === HubConnectionState.Disconnected) {
            // make sure the connection is established before joining the lesson
            await this.connection.start();
        }

        if (this.connection.state === HubConnectionState.Connecting) {
            await lastValueFrom(
                this.isConnected$.pipe(first(isConnected => isConnected))
            );
        }

        try {
            const lessonStateResponse: LessonStateResponse = await this.connection.invoke('JoinLesson', lessonId);
            this.joinedLessonId.set(lessonId);

            return lessonStateResponse;
        } catch (e) {
            const err = ensureError(e);

            const reason = lessonGoneReason(err.message);

            if (reason) {
                const error = new LessonStoppedError(reason, err);
                this.logger.error('Error while joining lesson: lesson is gone', error, { lessonId, reason });
                this.logger.trackEvent('JoinLessonFailed', { lessonId, reason });
                // Terminal: stop onreconnected() from re-invoking JoinLesson for a lesson that is gone.
                this.joinedLessonId.set(null);

                throw error;
            }

            // Possibly transient (server restart, timeout). Keep joinedLessonId so the next
            // reconnect retries the join instead of leaving this client silently detached.
            this.logger.error('Error while joining lesson', err, { lessonId });
            this.logger.trackEvent('JoinLessonFailed', { lessonId, reason: 'unknown' });

            throw err;
        }

    }

    public async leaveLesson()
    {
        const lessonIdToLeave = this.joinedLessonId();
        if (lessonIdToLeave) {
            await this.connection.send('LeaveLesson', lessonIdToLeave);
            this.joinedLessonId.set(null);
        }
    }


    public async stopConnection(): Promise<void> {
        this.joinedLessonId.set(null);
        this.isConnected.next(false);
        return await this.connection.stop();
    }

    public getConnectionID(): null | string {
        return this.connection.connectionId;
    }

    public getJoinedLessonId(): guid | null {
        return this.joinedLessonId();
    }

    public async sendGroupSignal(command: ClassroomCommand): Promise<void> {
        return await this.invoke('SendGroupCommand', command);
    }

    public async sendBeamerIsConnected(): Promise<void> {
        // TODO: add generic Command like SendToTeacher ?
        return await this.invoke('SendGroupCommand', {
            commandParam: "",
            commandType: CommandType.BEAMER_IS_CONNECTED,
        });
    }

    /**
     * Add a student(!) to a classroom.
     *
     * @param className
     */
    public async addToClass(className: string): Promise<void> {
        try {
            await this.connection.invoke('AddToGroup', className);
        } catch (err) {
            this.logger.error('addToClass failed', ensureError(err));
        }
    }

    public async sendCanvasContentUpdate(command: SlideCommands.UpdateRubberElementCommand): Promise<void> {
        return await this.send('SendCanvasContentUpdate', command);
    }

    public async sendStopLesson(command: LessonCommands.StopLessonCommand): Promise<void> {
        return await this.invoke('SendStopLesson', command);
    }

    public async sendVideoStartedPlaying(command: VideoCommands.PlayVideoCommand): Promise<void> {
        return await this.invoke('SendVideoStartedPlaying', command);
    }

    public async sendVideoPaused(command: VideoCommands.PauseVideoCommand): Promise<void> {
        return await this.invoke('SendVideoPaused', command);
    }

    public async sendVideoLoaded(command: VideoCommands.LoadVideoCommand): Promise<void> {
        return await this.invoke('SendVideoLoaded', command);
    }

    public async sendVideoSeeked(command: VideoCommands.SeekVideoCommand): Promise<void> {
        return await this.invoke('SendVideoSeeked', command);
    }

    public async sendActivateHotspotCommand(command: SlideCommands.SendActivateHotspotCommand): Promise<void> {
        return await this.invoke('sendActivateHotspotCommand', command);
    }

    public async sendUndoCommand(command: SlideCommands.SendUndoCommand): Promise<void> {
        return await this.send('sendUndoCommand', command);
    }

    public async sendRedoCommand(command: SlideCommands.SendRedoCommand): Promise<void> {
        return await this.send('sendRedoCommand', command);
    }

    public async sendNavigateToParentCommand(command: SlideCommands.SendNavigateToParentCommand): Promise<void> {
        return await this.send('sendNavigateToParentCommand', command);
    }

    public async sendPrefetchChapter(command: PrefetchImagesInChapterCommand): Promise<void> {
        return await this.send('sendPrefetchChapter', command);
    }

    public async sendPrefetchNextBasicSlides(command: PrefetchImagesInChapterCommand): Promise<void> {
        return await this.send('sendPrefetchExtensionSlides', command);
    }

    public async sendPrefetchExtensionSlides(command: PrefetchImagesInChapterCommand): Promise<void> {
        return await this.send('sendPrefetchExtensionSlides', command);
    }

    public async sendNavigateToPreviewSlide(command: SlideCommands.NavigateToPreviewSlideCommand): Promise<void> {
        return await this.invoke('SendNavigateToPreviewSlide', command);
    }

    public async sendNavigateToPreviewDriverscamAssignment(command: SlideCommands.NavigateToPreviewDriverscamAssignmentCommand)
        : Promise<void> {
        return await this.send('SendNavigateToPreviewDriverscamAssignment', command);
    }

    public async sendShowDifficultQuestionElement(command: SlideCommands.ShowDifficultQuestionElementCommand): Promise<void> {
        return await this.send('sendShowDifficultQuestionElement', command);
    }

    public async sendSettingsToBeamer(command: LessonCommands.SendSettingsToBeamerCommand): Promise<void> {
        return await this.invoke('SendSettingsToBeamer', command);
    }

    public async sendPushTheButtonCommand(command: any) {
        return await this.connection.invoke('SendGroupCommand', {
            commandType: CommandType.PUSH_THE_BUTTON_COMMAND,
            commandParam: JSON.stringify(command),
        });
    }

    public async sendMoodCheckSelectedTags(tags: string[]) {
        return await this.connection.invoke('StudentSubmittedTags', tags);
    }

    public async sendPracticeTrainerCommand(command: any) {
        return await this.connection.invoke('SendGroupCommand', {
            commandType: CommandType.PRACTICE_TRAINER_COMMAND,
            commandParam: JSON.stringify(command),
        });
    }

    public async sendIdentifyClient(client: SignalrLessonClient): Promise<void> {
        return await this.connection.send('IdentifyClient', client.connectionId);
    }

    public async sendPingUser(client: SignalrLessonClient): Promise<boolean> {
        return await this.connection.invoke('SendPing', client.connectionId);
    }

    /**
     * Send a message to the hub and wait until the hub responds.
     */
    private async invoke(commandName: string, command: ClassroomCommand): Promise<void> {
        try {
            return await this.connection.invoke(commandName, command);
        } catch (err) {
            const errorMsg = `Failed to invoke command: ${commandName}`;
            const error = new Error(errorMsg);
            this.logger.error(errorMsg, error, { originalError: err });
        }
    }

    /**
     * Send a message to the hub and do _not_ wait for any response.
     */
    private async send(commandName: string, command: ClassroomCommand): Promise<void> {
        try {
            return await this.connection.send(commandName, command);
        } catch (err) {
            const errorMsg = `Failed to send command: ${commandName}`;
            const error = new Error(errorMsg);
            this.logger.error(errorMsg, error, { originalError: err });
        }
    }

}
